import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Button } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
} from "recharts";
import {
  useLazyGetRevenueAnalyticsQuery,
  useLazyUsersGraphQuery,
  useLazyProductsGraphQuery,
  useLazyRevenueGraphQuery,
} from "../../service/revenueAnalytics";
import { useEffect, useState, useRef } from "react";

interface KpiData {
  totalUsers: number;
  newUsers: number;
  newProducts: number;
  totalProducts: number;
  totalRevenue: number;
}

const Analytics = () => {
  const downloadChartAsSVG = (
    chartRef: React.RefObject<HTMLDivElement>,
    filename: string
  ) => {
    if (!chartRef.current) return;

    const svgElement = chartRef.current.querySelector("svg");
    if (!svgElement) return;

    // Get the SVG source
    const serializer = new XMLSerializer();
    let source = serializer.serializeToString(svgElement);

    // Add namespaces if not present
    if (!source.match(/^<svg[^>]+xmlns="http\:\/\/www\.w3\.org\/2000\/svg"/)) {
      source = source.replace(
        /^<svg/,
        '<svg xmlns="http://www.w3.org/2000/svg"'
      );
    }
    if (!source.match(/^<svg[^>]+"http\:\/\/www\.w3\.org\/1999\/xlink"/)) {
      source = source.replace(
        /^<svg/,
        '<svg xmlns:xlink="http://www.w3.org/1999/xlink"'
      );
    }

    // Add XML declaration
    source = '<?xml version="1.0" standalone="no"?>\r\n' + source;

    // Convert SVG source to URI data scheme
    const url =
      "data:image/svg+xml;charset=utf-8," + encodeURIComponent(source);

    // Create download link
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}_${new Date().toISOString().split("T")[0]}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [kpiData, setKpiData] = useState<KpiData | null>(null);
  const [usersData, setUsersData] = useState<any>(null);
  const [usersProductsData, setUsersProductsData] = useState<any>(null);
  const [revenueData, setRevenueData] = useState<any>(null);
  const [type, setType] = useState("daily");
  const [getRevenueAnalytics] = useLazyGetRevenueAnalyticsQuery();
  const [usersGraph] = useLazyUsersGraphQuery();
  const [productsGraph] = useLazyProductsGraphQuery();
  const [revenueGraph] = useLazyRevenueGraphQuery();
  const usersChartRef = useRef<HTMLDivElement>(null);
  const productsChartRef = useRef<HTMLDivElement>(null);
  const revenueChartRef = useRef<HTMLDivElement>(null);

  const getData = async () => {
    try {
      const response = await getRevenueAnalytics();
      if (response?.data) {
        setKpiData(response?.data?.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getUserGraphData = async (type: string) => {
    try {
      const response = await usersGraph({ type });
      if (response?.data) {
        setUsersData(response?.data?.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getProductGraphData = async (type: string) => {
    try {
      const response = await productsGraph({ type });
      if (response?.data) {
        setUsersProductsData(response?.data?.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getRevenueGraphData = async (type: string) => {
    try {
      const response = await revenueGraph({ type });
      if (response?.data) {
        setRevenueData(response?.data?.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getData();
    getUserGraphData(type);
    getProductGraphData(type);
    getRevenueGraphData(type);
  }, []);

  return (
    <>
      <div className="main_title">
        <h1>Revenue & Analytics</h1>
      </div>

      <div className="stats_boxes gap_m">
        <div className="cards">
          <figure>
            <img src="/static/images/user_icon.svg" alt="Total Users" />
          </figure>
          <h2>{kpiData?.totalUsers}</h2>
          <p>Total Users</p>
        </div>

        <div className="cards">
          <figure>
            <img src="/static/images/user_icon.svg" alt="New Users" />
          </figure>
          <h2>{kpiData?.newUsers}</h2>
          <p>New Users</p>
        </div>

        <div className="cards">
          <figure>
            <img src="/static/images/user_icon.svg" alt="New Product" />
          </figure>
          <h2>{kpiData?.newProducts}</h2>
          <p>New Product</p>
        </div>

        <div className="cards">
          <figure>
            <img src="/static/images/user_icon.svg" alt="Total Products" />
          </figure>
          <h2>{kpiData?.totalProducts}</h2>
          <p>Total Products</p>
        </div>

        <div className="cards">
          <figure>
            <img src="/static/images/reward_icon.svg" alt="Total Revenue" />
          </figure>
          <h2>{kpiData?.totalRevenue} GH₵</h2>
          <p>Total Revenue</p>
        </div>
      </div>

      <div className="grid_sc gap_m">
        {/* Users & Listed Products Chart */}
        <div className="cards table_card">
          <div className="cards_header">
            <div className="left_s">
              <h2>Users</h2>
            </div>
            <div className="right_s">
              <p>
                <span onClick={() => getUserGraphData("daily")}>A day</span>
                <span onClick={() => getUserGraphData("weekly")}>A week</span>
                <span onClick={() => getUserGraphData("monthly")}>A month</span>
                {/* <span onClick={() => setType("yearly")}>A year</span> */}
              </p>
              <Button
                className="btn btn_black icon_btn"
                onClick={() => downloadChartAsSVG(usersChartRef, "users_chart")}
                title="Download as SVG"
              >
                <FileDownloadIcon />
              </Button>
            </div>
          </div>
          <div ref={usersChartRef}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={usersData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#38B6FF" />
                {/* <Bar dataKey="products" fill="#82ca9d" /> */}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="cards table_card">
          <div className="cards_header">
            <div className="left_s">
              <h2>Products</h2>
            </div>
            <div className="right_s">
              <p>
                <span onClick={() => getProductGraphData("daily")}>A day</span>
                <span onClick={() => getProductGraphData("weekly")}>
                  A week
                </span>
                <span onClick={() => getProductGraphData("monthly")}>
                  A month
                </span>
                {/* <span onClick={() => setType("yearly")}>A year</span> */}
              </p>
              <Button
                onClick={() =>
                  downloadChartAsSVG(productsChartRef, "products_chart")
                }
                className="btn btn_black icon_btn"
                title="Download as SVG"
              >
                <FileDownloadIcon />
              </Button>
            </div>
          </div>
          <div ref={productsChartRef}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={usersProductsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                {/* <Bar dataKey="users" fill="#38B6FF" /> */}
                <Bar dataKey="count" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="cards table_card">
          <div className="cards_header">
            <div className="left_s">
              <h2>Revenue</h2>
            </div>
            <div className="right_s">
              <p>
                <span onClick={() => getRevenueGraphData("daily")}>A day</span>
                <span onClick={() => getRevenueGraphData("weekly")}>
                  A week
                </span>
                <span onClick={() => getRevenueGraphData("monthly")}>
                  A month
                </span>
                {/* <span onClick={() => setType("yearly")}>A year</span> */}
              </p>
              <Button
                onClick={() =>
                  downloadChartAsSVG(revenueChartRef, "revenue_chart")
                }
                className="btn btn_black icon_btn"
                title="Download as SVG"
              >
                <FileDownloadIcon />
              </Button>
            </div>
          </div>
          <div ref={revenueChartRef}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#ffc658" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* <div className="cards cards table_card mt_20">
        <div className="main_title">
          <h2>Recent User Activities  <span> (Latest 10) </span> </h2>
        </div>
        <RecentActivities />
      </div> */}
    </>
  );
};

export default Analytics;
